/**
 * Plan display helpers — centralised so monthly_target contracts with
 * non-monthly cycle lengths (e.g. biweekly = 14 days) render consistently
 * across PlanSummaryCard, ContractsBreakdownCard, Wallet, and the pause
 * banners.
 */

import type { ContractType } from "@/contexts/ContractsContext";

const MS_PER_DAY = 86_400_000;

interface PlanLabel {
  label: string;
  description: string;
  cycleDays: number | null;
}

/** Round (cycleEnd - cycleStart) to a positive day count. */
export function getCycleDays(cycleStart: number, cycleEnd: number): number {
  if (!cycleStart || !cycleEnd || cycleEnd <= cycleStart) return 0;
  return Math.max(1, Math.round((cycleEnd - cycleStart) / MS_PER_DAY));
}

/**
 * Map (contractType, cycleDays) to a user-facing label and description.
 * Only monthly_target varies by cycle length; prepaid_package and trial
 * are cycle-agnostic.
 */
export function getPlanLabel(
  contractType: ContractType | string | null | undefined,
  cycleDays?: number | null,
): PlanLabel {
  if (contractType === "prepaid_package") {
    return { label: "Prepaid Package", description: "leads total", cycleDays: cycleDays ?? null };
  }
  if (contractType === "trial") {
    return { label: "Trial Plan", description: "leads included", cycleDays: cycleDays ?? null };
  }

  const days = cycleDays && cycleDays > 0 ? cycleDays : 30;
  if (days >= 6 && days <= 8) {
    return { label: "Weekly Plan", description: "leads per week", cycleDays: days };
  }
  if (days >= 13 && days <= 15) {
    return { label: "Biweekly Plan", description: "leads per 2 weeks", cycleDays: days };
  }
  if (days >= 28 && days <= 31) {
    return { label: "Monthly Plan", description: "leads per month", cycleDays: days };
  }
  return { label: `${days}-Day Plan`, description: `leads per ${days} days`, cycleDays: days };
}

/**
 * Self-service pause limit in days, mirroring the
 * pause_contract_self_service RPC formula:
 *   max(3, min(14, floor(cycle_days / 2)))
 *
 * Used for UI copy only — the RPC remains the authoritative validator
 * and is always called server-side.
 */
export function getMaxPauseDays(cycleDays: number | null | undefined): number {
  if (!cycleDays || cycleDays <= 0) return 14;
  return Math.max(3, Math.min(14, Math.floor(cycleDays / 2)));
}
